/* eslint-disable react-hooks/rules-of-hooks -- pre-existing violations unmasked by #1772; tracked + to be fixed in #1842. Do not add new conditional hooks here. */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  List,
  Typography,
  Switch,
  FormControlLabel,
  CircularProgress,
  useTheme,
} from '@mui/material'
import { useList, useOne } from '@refinedev/core'
import { Fragment, useEffect, useState } from 'react'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ReactMarkdown from 'react-markdown'

import {
  formatDate,
  roundIndexFromKey,
  leafFieldFromKey,
  describeRound,
  describeProvenance,
  addedFieldName,
} from './revisionFormatting'

interface OpportunityRevisionProps {
  opportunityUuid: number
}
interface DiffData {
  values_changed?: Record<
    string,
    {
      diff: string
      old_value: any
      new_value: any
    }
  >
  dictionary_item_removed?: string[]
  dictionary_item_added?: Record<string, any>
  iterable_item_removed?: Record<string, any>
  iterable_item_added?: Record<string, any>
  type_changes?: Record<string, any>
}

const OPPORTUNITY_FIELD_NAME_MAP: Record<string, string> = {
  provenance: 'Provenance',
  uuid: 'UUID',
  funder_uuid: 'Funder',
  criteria: 'Criteria',
  exclusions: 'Exclusions',
  fund_name: 'Fund Name',
  timing: 'Timing',
  amount: 'Amount',
  questions: 'Questions',
  notes: 'Notes',
  profile_embedding: 'Profile Embedding',
  profile_text: 'Profile Text',
  draft_profile_text: 'Draft Profile Text',
  region_ids: 'Locations',
  is_relational: 'Is Relational',
  description: 'Description',
  always_open: 'Always Open',
  application_open_date: 'Application Open Date',
  application_close_date: 'Application Close Date',
  decision_date: 'Decision Date',
  application_info: 'Application Info',
  references: 'References',
  flag_for_updates: 'Flag for Updates',
  funding_rounds: 'Funding Rounds',
  funding_round: 'Funding Rounds',
  estimate_date_flag: 'Dates Estimated',
  active: 'Active',
  next_check_in_date: 'Next Check In Date',
  is_live: 'Is Live',
  kaupapa_maori: 'Kaupapa Māori',
  gambling_proceeds: 'Gambling Proceeds',
  is_international: 'Is International',
  application_link: 'Application Link',
  only_funds_registered_charities: 'Only Funds Registered Charities',
  max_rank_is_4: 'Max. Rank is 4',
  general_info: 'General Info',
  iwi: 'Iwi',
  max_amount: 'Max Amount',
  min_amount: 'Min Amount',
  no_min_max_amount: 'No Min Max Amount',
  opportunity_link: 'Opportunity Link',
  email_address: 'Email Address',
}

const fieldOrder: Record<string, number> = {
  fund_name: 1,
  funder_uuid: 2,
  amount: 3,
  timing: 4,
  criteria: 5,
  exclusions: 6,
  region: 7,
  always_open: 8,
  is_relational: 9,
  description: 10,
  profile_text: 11,
  draft_profile_text: 12,
  questions: 13,
  notes: 14,
}

function translateFieldName(fieldPath: string): string {
  if (
    fieldPath.includes('funding_round') ||
    fieldPath.includes('funding_rounds')
  ) {
    const fieldName = extractFieldName(fieldPath)
    const fieldMap = OPPORTUNITY_FIELD_NAME_MAP

    return fieldMap[fieldName] || fieldName
  }

  const fieldName = fieldPath.includes("root['")
    ? fieldPath.split("root['")[1]?.split("']")[0]
    : fieldPath

  const fieldMap = OPPORTUNITY_FIELD_NAME_MAP
  return fieldMap[fieldName] || fieldName
}

function extractFieldName(key: string): string {
  const keyWithoutRoot = key.replace(/^root\[\d+\]/, '')

  if (keyWithoutRoot.startsWith("['funding_round'][")) {
    let actualField = keyWithoutRoot.split("['funding_round']")[1]
    actualField = actualField.slice(2).split("']")[0]
    return actualField
  }

  const match = keyWithoutRoot.match(
    /\['(funding_round|funding_rounds)'\]\[(\d+)\]/,
  )

  return match ? match[1] : key
}

function getRegionName(regionId: number) {
  const { data: regionData } = useOne({
    resource: 'regions',
    id: regionId,
    errorNotification: (error, values, resource) => {
      if (error?.response?.status === 404) {
        return false // Suppress 404 error for deleted regions
      }
      return {
        message: 'Failed to load region',
        description: error?.message || `Error loading region ${regionId}`,
        type: 'error',
      }
    },
  })
  return regionData?.data?.name || 'Unknown Location (Deleted)'
}

function getIwiName(iwiId: number) {
  const { data: iwiData } = useOne({
    resource: 'iwi',
    id: iwiId,
    errorNotification: (error, values, resource) => {
      if (error?.response?.status === 404) {
        return false // Suppress 404 error for deleted iwi
      }
      return {
        message: 'Failed to load iwi',
        description: error?.message || `Error loading iwi ${iwiId}`,
        type: 'error',
      }
    },
  })
  return iwiData?.data?.name || 'Unknown Iwi (Deleted)'
}

function getFunderName(funderUuid: string) {
  const { data: funderData } = useOne({
    resource: 'funders',
    id: funderUuid,
    errorNotification: (error, values, resource) => {
      return {
        message: 'Failed to load funder',
        description: error?.message || `Error loading funder`,
        type: 'error',
      }
    },
  })
  return funderData?.data?.name || 'Unknown Funder'
}

export default function OpportunityRevisions({
  opportunityUuid,
}: OpportunityRevisionProps) {
  const [loading, setLoading] = useState(true)

  const { data: opportunityRevisionData, isLoading } = useList({
    resource: 'opportunity-revisions',
    meta: {
      opportunity_uuid: opportunityUuid as number,
    },
    errorNotification: (error, values, resource) => {
      return {
        message: 'Opportunity Revisions Not Found',
        description:
          error?.message || `Error loading revisions for opportunity`,
        type: 'error',
      }
    },
  })

  // debounce to avoid rendering data too quickly
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false)
    }, 500)

    return () => clearTimeout(timer)
  }, [opportunityUuid])

  if (isLoading || loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (
    !opportunityRevisionData ||
    !Object.keys(opportunityRevisionData).length
  ) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Typography variant="h5" sx={{ textAlign: 'center' }}>
          Revisions
        </Typography>
        <Typography variant="body2" sx={{ textAlign: 'center' }}>
          No revision data found
        </Typography>
      </Box>
    )
  }

  return (
    <>
      <Box padding={2}>
        <Typography variant="h4" gutterBottom>
          Revisions
        </Typography>
        <List>
          {Object.entries(opportunityRevisionData).map(([name, versions]) => (
            <RevisionsExpandable key={name} name={name} versions={versions} />
          ))}
        </List>
      </Box>
    </>
  )
}

function RevisionsExpandable({
  name,
  versions,
}: {
  name: string
  versions: any[]
}) {
  if (!versions || versions.length === 0) return null
  const sortedVersions = Object.values(versions).sort(
    (a, b) => b.version - a.version,
  )

  if (sortedVersions.length === 0) return null

  return (
    <Box>
      <Typography variant="h5" sx={{ my: 1 }}>
        Latest Version
      </Typography>

      <VersionDetails version={sortedVersions[0]} />
      {sortedVersions.length > 1 && (
        <Box marginTop={5}>
          <Box>
            <Typography variant="h5" sx={{ my: 1 }}>
              Previous Versions
            </Typography>
          </Box>

          {sortedVersions.slice(1).map((version, index) => (
            <VersionDetails
              key={`version-${index}-${version.version}`}
              version={version}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

function VersionDetails({ version }: { version: any }) {
  if (version == null || version.diff == null) {
    return null
  }
  const diffData = version.diff

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ width: '33%', flexShrink: 0, fontWeight: 'bold' }}>
          Version {version.version}
        </Typography>
        <Typography sx={{ color: 'text.secondary' }}>
          {new Date(version.date).toLocaleString('en-NZ', { hour12: true })}
        </Typography>
      </AccordionSummary>

      <AccordionDetails>
        <Typography variant="subtitle1">
          <strong>Data Entry By:</strong> {version.data_entry_by}
        </Typography>
        <Typography variant="subtitle1" marginBottom={3}>
          <strong>Date:</strong>{' '}
          {new Date(version.date).toLocaleString('en-NZ', { hour12: true })}
        </Typography>
        <DiffSection diffData={diffData as DiffData} data={version.data} />
      </AccordionDetails>
    </Accordion>
  )
}

function renderUnifiedDiff(diff: string) {
  return (
    <Box
      component="pre"
      sx={{
        background: '#282a36',
        color: '#f8f8f2',
        padding: '10px',
        borderRadius: '4px',
        fontFamily: 'monospace',
        overflowX: 'wrap',
        whiteSpace: 'pre-wrap',
      }}
    >
      {diff.split('\n').map((line, index) => (
        <Typography
          key={index}
          component="span"
          sx={{
            display: 'block',
            color: line.startsWith('-')
              ? '#ff5555' // Removed
              : line.startsWith('+')
              ? '#50fa7b' // Added
              : '#f8f8f2', // Context
          }}
        >
          {line}
        </Typography>
      ))}
    </Box>
  )
}

function DiffSection({ diffData, data }: { diffData: DiffData; data?: any }) {
  if (!diffData) return null

  const [showDiff, setShowDiff] = useState(false)

  const theme = useTheme()
  const lightOrDarkMode = theme.palette.mode

  function handleToggleDiff() {
    setShowDiff(!showDiff)
  }

  return (
    <Box marginBottom={4}>
      {/* Added or Changed Types (eg. New or removed db columns) */}
      {diffData.type_changes && (
        <Box marginBottom={2}>
          {Object.entries(diffData.type_changes).map(([key, value], index) =>
            value.old_type == "<class 'NoneType'>" ? (
              <Fragment key={`type-added-change-${index}-${key}`}>
                <Typography
                  variant="h6"
                  sx={{ mb: 1, textDecoration: 'underline' }}
                >
                  New Field Added
                </Typography>
                <Typography>
                  <strong>{translateFieldName(key)}</strong> field was added
                  with value:{' '}
                  {key.includes('iwi')
                    ? value.new_value
                        .map((id: any) => getIwiName(id))
                        .join(', ')
                    : JSON.stringify(value.new_value)}
                </Typography>
              </Fragment>
            ) : value.new_type == "<class 'NoneType'>" ? (
              <Fragment key={`type-removed-change-${index}-${key}`}>
                <Typography
                  variant="h6"
                  sx={{ mb: 1, textDecoration: 'underline' }}
                >
                  New Field Removed
                </Typography>
                <Typography>
                  <strong>{translateFieldName(key)}</strong> field was removed
                  with value: {JSON.stringify(value.old_value)}
                </Typography>
              </Fragment>
            ) : null,
          )}
        </Box>
      )}
      {/* Removed Items */}
      {diffData.dictionary_item_removed &&
        diffData.dictionary_item_removed.length > 0 && (
          <Box marginBottom={2}>
            <Typography
              variant="h6"
              sx={{ mb: 1, textDecoration: 'underline' }}
            >
              Removed Items
            </Typography>
            {diffData.dictionary_item_removed?.map(
              (item: string, index: number) => (
                <Typography key={`removed-item-${item}-${index}`}>
                  <strong>{translateFieldName(item)}</strong> field was removed.
                </Typography>
              ),
            )}
          </Box>
        )}

      {/* Added Items */}
      {diffData.dictionary_item_added && (
        <Box marginBottom={2}>
          <Typography variant="h6" sx={{ mb: 1, textDecoration: 'underline' }}>
            Added Items
          </Typography>
          {(Array.isArray(diffData.dictionary_item_added)
            ? diffData.dictionary_item_added
            : Object.values(diffData.dictionary_item_added)
          ).map((path: unknown, index: number) => {
            // GH #1846 — entries are DeepDiff PATH STRINGS, not values, so the
            // actual value has to come from the revision snapshot. `provenance`
            // (the why/who of a direct write) renders as labelled rows;
            // everything else keeps the generic JSON fallback.
            const field = addedFieldName(path)
            const added = field ? data?.[field] : undefined
            const provenanceRows =
              field === 'provenance' ? describeProvenance(added) : null
            return (
              <Box key={`added-item-${index}-${String(path)}`} sx={{ mb: 1 }}>
                <Typography>
                  <strong>{translateFieldName(String(path))}</strong>
                  {provenanceRows
                    ? ''
                    : ` field was added with value: ${JSON.stringify(added)}`}
                </Typography>
                {provenanceRows && (
                  <Box sx={{ pl: 2 }}>
                    {provenanceRows.map((row) => (
                      <Typography key={row.label} variant="body2">
                        <strong>{row.label}:</strong> {row.value}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            )
          })}
        </Box>
      )}

      {/* Values Changed */}
      {diffData.values_changed && (
        <Box marginBottom={2}>
          {Object.entries(diffData.values_changed).map(
            ([key, change], index) => (
              <Box
                key={`changed-item-${key}-${index}-${change}`}
                marginBottom={2}
              >
                {key === 'root' ? (
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      <strong>New Entry</strong>
                    </Typography>
                    <Box
                      bgcolor={lightOrDarkMode === 'light' ? '#f5f5f5' : '#333'}
                      borderRadius={1}
                      padding={2}
                      mb={2}
                    >
                      {Object.entries(change.new_value || {})
                        .sort(([fieldA], [fieldB]) => {
                          return (
                            (fieldOrder[fieldA] || Infinity) -
                            (fieldOrder[fieldB] || Infinity)
                          )
                        })
                        .map(([field, value], fieldIndex) => (
                          <Box
                            key={`${value}-${field}-${fieldIndex}`}
                            sx={{ mb: 1 }}
                          >
                            <Typography variant="body2">
                              <strong>{translateFieldName(field)}</strong>
                            </Typography>
                            {field === 'region_ids' ? (
                              <Typography variant="body1">
                                {Array.isArray(value)
                                  ? value.map((regionId: number) =>
                                      getRegionName(regionId),
                                    )
                                  : 'None'}
                              </Typography>
                            ) : field === 'iwi' ? (
                              <Typography variant="body1">
                                {Array.isArray(value)
                                  ? value.map((regionId: number) =>
                                      getIwiName(regionId),
                                    )
                                  : 'None'}
                              </Typography>
                            ) : (
                              <>
                                {field === 'funder_uuid' ? (
                                  getFunderName(String(value))
                                ) : String(value) !== 'null' &&
                                  String(value) !== '' ? (
                                  <ReactMarkdown
                                    className={`markdown-${lightOrDarkMode}`}
                                  >
                                    {typeof value === 'boolean'
                                      ? value === true
                                        ? 'Yes'
                                        : 'No'
                                      : String(value)}
                                  </ReactMarkdown>
                                ) : (
                                  'None'
                                )}
                              </>
                            )}
                          </Box>
                        ))}
                    </Box>
                  </Box>
                ) : (
                  <>
                    <Typography
                      variant="h6"
                      sx={{ mb: 1, textDecoration: 'underline' }}
                    >
                      Changed Values
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {key.includes("['funding_round") ? (
                        <>
                          <strong>
                            {describeRound(data, roundIndexFromKey(key))}
                          </strong>{' '}
                          — {translateFieldName(leafFieldFromKey(key))} was
                          changed:
                        </>
                      ) : (
                        <>
                          <strong>{translateFieldName(key)}</strong> field was
                          changed:
                        </>
                      )}
                    </Typography>
                    <Typography variant="body2">
                      <strong>New Value</strong>
                    </Typography>
                    <Box
                      bgcolor="#f5f5f5"
                      borderRadius={1}
                      paddingX={2}
                      paddingY={1}
                      mb={2}
                    >
                      {key === 'funder_uuid' ? (
                        getFunderName(change.new_value)
                      ) : typeof change.new_value === 'string' ? (
                        <ReactMarkdown
                          className={`markdown-${lightOrDarkMode}`}
                        >
                          {change.new_value}
                        </ReactMarkdown>
                      ) : typeof change.new_value === 'boolean' ? (
                        change.new_value === true ? (
                          'Yes'
                        ) : (
                          'No'
                        )
                      ) : typeof change.new_value === 'object' ? (
                        change.new_value?.funding_round && (
                          <>
                            <Typography variant="body1">
                              Application Open Date:{' '}
                              {change.new_value.funding_round
                                .application_open_date || 'None'}
                            </Typography>
                            <Typography variant="body1">
                              Application Close Date:{' '}
                              {change.new_value.funding_round
                                .application_close_date || 'None'}
                            </Typography>
                            <Typography variant="body1">
                              Decision Date:{' '}
                              {change.new_value.funding_round.decision_date ||
                                'None'}
                            </Typography>
                            <Typography variant="body1">
                              Dates Estimated:{' '}
                              {change.new_value.funding_round.estimate_date_flag
                                ? 'Yes'
                                : 'No'}
                            </Typography>
                          </>
                        )
                      ) : (
                        JSON.stringify(change.new_value)
                      )}
                    </Box>
                    <Typography variant="body2">
                      <strong>Old Value</strong>
                    </Typography>
                    <Box
                      bgcolor="#f5f5f5"
                      borderRadius={1}
                      paddingX={2}
                      paddingY={1}
                      mb={2}
                    >
                      {key === 'funder_uuid' ? (
                        getFunderName(change.old_value)
                      ) : typeof change.old_value === 'string' ? (
                        <ReactMarkdown
                          className={`markdown-${lightOrDarkMode}`}
                        >
                          {change.old_value}
                        </ReactMarkdown>
                      ) : typeof change.old_value === 'boolean' ? (
                        change.old_value === true ? (
                          'Yes'
                        ) : (
                          'No'
                        )
                      ) : typeof change.old_value === 'object' ? (
                        change.old_value?.funding_round && (
                          <>
                            <Typography variant="body1">
                              Application Open Date:{' '}
                              {change.old_value.funding_round
                                .application_open_date || 'None'}
                            </Typography>
                            <Typography variant="body1">
                              Application Close Date:{' '}
                              {change.old_value.funding_round
                                .application_close_date || 'None'}
                            </Typography>
                            <Typography variant="body1">
                              Decision Date:{' '}
                              {change.old_value.funding_round.decision_date ||
                                'None'}
                            </Typography>
                            <Typography variant="body1">
                              Dates Estimated:{' '}
                              {change.old_value.funding_round.estimate_date_flag
                                ? 'Yes'
                                : 'No'}
                            </Typography>
                          </>
                        )
                      ) : (
                        JSON.stringify(change.old_value)
                      )}
                    </Box>
                    {change.diff && (
                      <FormControlLabel
                        control={<Switch onChange={handleToggleDiff} />}
                        label="Show Diff View"
                      />
                    )}
                  </>
                )}
                {change.diff && showDiff && renderUnifiedDiff(change.diff)}
              </Box>
            ),
          )}
        </Box>
      )}

      {/* Removed Iterable Items ie. Locations, Funding Rounds */}
      {diffData.iterable_item_removed && (
        <Box marginBottom={2}>
          <Typography variant="h6" sx={{ mb: 1, textDecoration: 'underline' }}>
            Removed Items
          </Typography>
          {Object.entries(diffData.iterable_item_removed).map(
            ([key, value], index) =>
              key.includes("root['region_ids']") ? (
                <Typography key={`region-${key}-${index}`}>
                  Location was deleted with value: {getRegionName(value)}
                </Typography>
              ) : key.includes("root['iwi']") ? (
                <Typography key={`iwi-${key}-${index}`}>
                  Iwi was deleted with value: {getIwiName(value)}
                </Typography>
              ) : key.includes("root['funding_rounds']") ? (
                <Box key={`funding-round-${key}-${index}`}>
                  <Typography>
                    <strong>Funding Round was deleted with values </strong>
                  </Typography>
                  <Typography>
                    Application Open Date:{' '}
                    {value.funding_round.application_open_date
                      ? formatDate(value.funding_round.application_open_date)
                      : 'None'}
                  </Typography>
                  <Typography>
                    Application Close Date:{' '}
                    {value.funding_round.application_close_date
                      ? formatDate(value.funding_round.application_close_date)
                      : 'None'}
                  </Typography>
                  <Typography>
                    Decision Date:{' '}
                    {value.funding_round.decision_date
                      ? formatDate(value.funding_round.decision_date)
                      : 'None'}
                  </Typography>
                  <Typography>
                    Dates Estimated:{' '}
                    {value.funding_round.estimate_date_flag ? 'Yes' : 'No'}
                  </Typography>
                </Box>
              ) : (
                <Typography key={`removed-item-${key}-${index}`}>
                  <strong>{translateFieldName(key)}</strong> field was removed
                  with value: {JSON.stringify(value)}
                </Typography>
              ),
          )}
        </Box>
      )}

      {/* Added Iterable Items ie. Locations */}
      {diffData.iterable_item_added && (
        <Box marginBottom={2}>
          <Typography variant="h6" sx={{ mb: 1, textDecoration: 'underline' }}>
            Added Items
          </Typography>
          {Object.entries(diffData.iterable_item_added).map(
            ([key, value], index) =>
              key.includes("root['region_ids']") ? (
                <Typography key={`region-${key}-${index}`}>
                  New location was added with value: {getRegionName(value)}
                </Typography>
              ) : key.includes("root['iwi']") ? (
                <Typography key={`iwi-${key}-${index}`}>
                  New iwi was added with value: {getIwiName(value)}
                </Typography>
              ) : key.includes("root['funding_rounds']") ? (
                <Box key={`funding-round-${key}-${index}`}>
                  <Typography>
                    <strong>New funding round was added </strong>
                  </Typography>
                  <Typography>
                    Application Open Date:{' '}
                    {value.funding_round.application_open_date
                      ? formatDate(value.funding_round.application_open_date)
                      : 'None'}
                  </Typography>
                  <Typography>
                    Application Close Date:{' '}
                    {value.funding_round.application_close_date
                      ? formatDate(value.funding_round.application_close_date)
                      : 'None'}
                  </Typography>
                  <Typography>
                    Decision Date:{' '}
                    {value.funding_round.decision_date
                      ? formatDate(value.funding_round.decision_date)
                      : 'None'}
                  </Typography>
                  <Typography>
                    Dates Estimated:{' '}
                    {value.funding_round.estimate_date_flag ? 'Yes' : 'No'}
                  </Typography>
                </Box>
              ) : (
                <Typography key={`added-item-${key}-${index}`}>
                  <strong>{translateFieldName(key)}</strong> field was added
                  with value: {JSON.stringify(value) || 'None'}
                </Typography>
              ),
          )}
        </Box>
      )}
    </Box>
  )
}
